import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { PatientDrugInteraction } from '@/types/patient-chart-api';

/**
 * Patient drug-interaction alerts — Chart > D/I tab (PROD-737 PR B).
 *
 * Wire (Flutter `patient_chart_data_source_impl.dart:927`):
 *
 *   GET /api/EPriscription/GetPatientInteractionAlertDetails?PatientId={id}
 *       → PatientDrugInteraction[]
 *
 * **Backend typo preserved**: the service base is `/api/EPriscription`
 * (should be "EPrescription"). Flutter uses the typo verbatim because
 * the .NET route is defined with the typo. DO NOT "fix" — it 404s on
 * the correctly-spelled path.
 *
 * Returns the list of active interaction alerts attached to this
 * patient's medications. Empty when there are no triggered alerts —
 * that's the clinically desirable state. Computed server-side when a
 * new Rx is drafted (the `/PrescriptionAlert` POST endpoint, used by
 * the encounter Rx authoring flow); the GET endpoint here just
 * surfaces the persisted alerts on the chart.
 */

export function usePatientDrugInteractions(
  patientId: string | null | undefined,
) {
  return useQuery({
    queryKey: ['patient-drug-interactions', patientId],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ PatientId: patientId ?? '' });
      return get<PatientDrugInteraction[]>(
        `/api/EPriscription/GetPatientInteractionAlertDetails?${qs.toString()}`,
        { signal },
      );
    },
    enabled: !!patientId,
    staleTime: 60_000,
  });
}
